import React, { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LogoutConfirmation from "./LogoutConfirmation";
import PortalPopup from "./PortalPopup";
import DebtCollectionNav from "./DebtCollectionNav";
import { Menu, X } from "lucide-react";

const NavigationPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const openLogoutConfirmation = useCallback(() => {
    setLogoutConfirmationOpen(true);
  }, []);

  const closeLogoutConfirmation = useCallback(() => {
    setLogoutConfirmationOpen(false);
  }, []);

  const goToSettingPage = useCallback(() => {
    navigate("/invoicereminder");
  }, []);

  const toggleNavbar = () => {
    setIsOpen(!isOpen);
  };

  // For settings tab
  const [activeTab, setActiveTab] = useState(null);

  // ✅ Define all settings-related routes
  const settingsRoutes = [
    "/invoicereminder",
    "/setting-billing",
    "/setting-security",
    "/setting-account",
    "/setting-account2",
    "/setting-account3",
    "/setting-account4",
    "/datamanagement",
    "/setting-help&support",
  ];

  // ✅ Check if current route belongs to Settings
  const isSettingsActive = settingsRoutes.some((path) =>
    location.pathname.startsWith(path)
  );

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    if (tabName === "invoicereminder") {
      goToSettingPage();
    }
  };

  const navItems = [
    {
      text: "Dashboard",
      bgColor: "bg-transparent",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50/30",
      hoverTextColor: "text-primary-primary-500",
      path: "/debtordashboard",
      dropdown: false,
    },

    {
      text: "Debt collection",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/debt-collection",
      dropdown: true,
      icon: "assets/arrow-down.svg",
    },

    {
      text: "AI indicators",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/ai1",
      dropdown: false,
    },
    {
      text: "Invoices ",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/xero-invoices",
      dropdown: false,
      icon: "",
    },
    {
      text: "Statements",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/statements",
      dropdown: false,
      icon: "",
    },
  ];

  return (
    <>
      <div className="w-full bg-white/30 box-border flex flex-col md:flex-row items-start md:items-center justify-between py-4 md:py-8 px-4 lg:px-24 text-left text-base text-neutrals-neutral-300 font-open-sans-ff-body-strong border-b-[1px] border-solid border-neutrals-neutral-100 backdrop-blur-md fixed top-0 left-0 z-50">
        <div className="w-full md:w-auto flex flex-row items-center justify-between md:justify-start md:gap-[51px]">
          {/* Logo */}
          <img src="assets/main-logo.svg" className="h-14" />

          {/* Desktop Navigation - now aligned with logo */}
          <div className="hidden md:flex flex-row gap-4">
            <DebtCollectionNav
              navItems={navItems}
              activePath={location.pathname}
            />
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden bg-transparent" onClick={toggleNavbar}>
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Desktop System Icons - now properly aligned to the right */}
        <div className="hidden md:flex flex-row items-center justify-end gap-[6px]">
          <div
            className={`rounded-3xs flex flex-row items-start justify-start p-2.5 cursor-pointer transition-all duration-200 
              ${
                isSettingsActive
                  ? "bg-neutrals-white-light" // active background
                  : "bg-transparent" // default background
              }`}
            onClick={() => navigate("/invoicereminder")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                stroke="#576e7e"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M2 12.88v-1.76c0-1.04.85-1.9 1.9-1.9 1.81 0 2.55-1.28 1.64-2.85-.52-.9-.21-2.07.7-2.59l1.73-.99c.79-.47 1.81-.19 2.28.6l.11.19c.9 1.57 2.38 1.57 3.29 0l.11-.19c.47-.79 1.49-1.07 2.28-.6l1.73.99c.91.52 1.22 1.69.7 2.59-.91 1.57-.17 2.85 1.64 2.85 1.04 0 1.9.85 1.9 1.9v1.76c0 1.04-.85 1.9-1.9 1.9-1.81 0-2.55 1.28-1.64 2.85.52.91.21 2.07-.7 2.59l-1.73.99c-.79.47-1.81.19-2.28-.6l-.11-.19c-.9-1.57-2.38-1.57-3.29 0l-.11.19c-.47.79-1.49 1.07-2.28.6l-1.73-.99a1.899 1.899 0 0 1-.7-2.59c.91-1.57.17-2.85-1.64-2.85-1.05 0-1.9-.86-1.9-1.9Z"
                stroke="#576e7e"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>
          </div>
          {/* <div className="rounded-3xs bg-neutrals-neutral-50 flex flex-row items-start justify-start p-2.5">
            <div className="flex flex-row items-start justify-start relative gap-[10px]">
              <img
                className="w-5 relative h-5 z-[0]"
                alt=""
                src="assets/notification-default.svg"
              />
            </div>
          </div> */}
          <div
            className="flex flex-row items-start justify-end p-2.5 cursor-pointer"
            onClick={openLogoutConfirmation}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                stroke={activeTab === "invoicereminder" ? "#0046FF" : "#576e7e"}
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M8.9 7.56c.31-3.6 2.16-5.07 6.21-5.07h.13c4.47 0 6.26 1.79 6.26 6.26v6.52c0 4.47-1.79 6.26-6.26 6.26h-.13c-4.02 0-5.87-1.45-6.2-4.99M15 12H3.62M5.85 8.65L2.5 12l3.35 3.35"
              ></path>
            </svg>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden w-full flex flex-col items-center mt-4 space-y-4">
            <DebtCollectionNav navItems={navItems} isMobile={true} />

            <div className="w-full flex flex-row items-center justify-around py-3 border-t border-neutrals-neutral-100">
              <div
                className={`p-2 cursor-pointer transition-all duration-200 ${
                  isSettingsActive
                    ? "bg-neutrals-white-light"
                    : "bg-transparent"
                }`}
                onClick={() => navigate("/invoicereminder")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                    stroke="#576e7e"
                    stroke-width="1.5"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                  <path
                    d="M2 12.88v-1.76c0-1.04.85-1.9 1.9-1.9 1.81 0 2.55-1.28 1.64-2.85-.52-.9-.21-2.07.7-2.59l1.73-.99c.79-.47 1.81-.19 2.28.6l.11.19c.9 1.57 2.38 1.57 3.29 0l.11-.19c.47-.79 1.49-1.07 2.28-.6l1.73.99c.91.52 1.22 1.69.7 2.59-.91 1.57-.17 2.85 1.64 2.85 1.04 0 1.9.85 1.9 1.9v1.76c0 1.04-.85 1.9-1.9 1.9-1.81 0-2.55 1.28-1.64 2.85.52.91.21 2.07-.7 2.59l-1.73.99c-.79.47-1.81.19-2.28-.6l-.11-.19c-.9-1.57-2.38-1.57-3.29 0l-.11.19c-.47.79-1.49 1.07-2.28.6l-1.73-.99a1.899 1.899 0 0 1-.7-2.59c.91-1.57.17-2.85-1.64-2.85-1.05 0-1.9-.86-1.9-1.9Z"
                    stroke="#576e7e"
                    stroke-width="1.5"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                </svg>
              </div>
              {/* <div className="p-2">
                <img
                  className="w-6 h-6"
                  alt=""
                  src="assets/notification-default.svg"
                />
              </div> */}
              <div
                className="p-2 cursor-pointer"
                onClick={openLogoutConfirmation}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    stroke="#576e7e"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M8.9 7.56c.31-3.6 2.16-5.07 6.21-5.07h.13c4.47 0 6.26 1.79 6.26 6.26v6.52c0 4.47-1.79 6.26-6.26 6.26h-.13c-4.02 0-5.87-1.45-6.2-4.99M15 12H3.62M5.85 8.65L2.5 12l3.35 3.35"
                  ></path>
                </svg>
              </div>
            </div>
          </div>
        )}
      </div>

      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
    </>
  );
};

export default NavigationPanel;
